<template>
    <main
        v-if="data"
        class="relative pb-10 pt-25 md:pb-15 md:pt-36">
        <TheBreadcrumbs
            v-if="isCromi()"
            :items="breadcrumbs"
            class="mb-5" />

        <AtomTitle
            level="1"
            class="container">
            Профессиональный блог
        </AtomTitle>
        <CategoryMultiselect
            v-if="data?.data.all_categories?.data?.length"
            v-model="l5Filters.categories"
            :categories="data.data.all_categories.data"
            class="mt-10"
            @update:model-value="
                l5UpdateQuery({
                    categories: l5Filters.categories,
                    page: 1,
                })
            " />

        <LazyApiSection
            recovery-required
            :recovery-ready="!!articles && !pending"
            :recovery-transitioning="recoveryTransitioning"
            min-height="32rem"
            @visible="loadDeferredProContent">
            <Transition
                @before-leave="recoveryTransitioning = true"
                @after-enter="recoveryTransitioning = false"
                name="fast-fade"
                mode="out-in">
                <section
                    v-if="articles?.data.length && !pending"
                    class="mt-10">
                    <div
                        class="container grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                        <NewsCard
                            v-for="article in articles.data"
                            :key="article.slug"
                            card-type="article"
                            hide-date
                            :item="article" />
                    </div>
                    <PaginationSection
                        v-if="articles.meta?.pagination"
                        show-items-per-page
                        pages-name="записей"
                        :pagination="articles.meta.pagination"
                        :items-count="articles?.data.length"
                        class="mt-10" />
                </section>
                <section
                    v-else-if="!articles?.data.length && !pending"
                    class="container relative mt-10 flex items-center justify-center overflow-hidden rounded-20 py-40">
                    <div
                        class="relative space-y-7 text-center text-24/133 font-semibold">
                        <p>
                            Таких статей у нас еще нет, но есть много других
                            интересных.
                        </p>
                        <p>Измените параметры фильтров и попробуйте еще раз</p>
                    </div>
                    <div class="absolute inset-0 size-full">
                        <div class="bg-noise"></div>
                        <div
                            class="absolute left-0 top-0 aspect-square w-1/3 -translate-x-1/2 -translate-y-1/2 animate-slow-pulse rounded-full bg-accent/60 blur-[6rem] delay-300" />
                        <div
                            class="absolute bottom-0 right-0 aspect-square w-1/3 translate-x-1/2 translate-y-1/2 animate-slow-pulse rounded-full bg-accent/50 blur-[6rem] delay-500" />
                    </div>
                </section>
                <section
                    v-else
                    class="flex h-[50vh] w-full items-center justify-center">
                    <BaseLoader />
                </section>
            </Transition>
        </LazyApiSection>
    </main>
</template>

<script setup lang="ts">
import z from 'zod'
import type { ProPage } from '~/types/pages.types'

import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import BaseLoader from '~/components/base/base-loader/BaseLoader.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import CategoryMultiselect from '~/components/particles/category-multiselect/CategoryMultiselect.vue'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import NewsCard from '~/components/particles/news-section/NewsCard.vue'
import PaginationSection from '~/components/particles/pagination-section/PaginationSection.vue'

const modalStore = useModalStore()
const recoveryTransitioning = ref(false)

const breadcrumbs: BreadcrumbItem[] = [
    {
        label: 'Главная',
        to: '/',
    },
    {
        label: 'Профессиональный блог',
    },
]

const schema = z.object({
    categories: z.array(z.number()),
})

const { l5Filters, l5Query, l5UpdateQuery } = useL5(schema, {
    fromQuery: true,
    include: ['all_categories'],
    queryMap: {
        categories: 'categories_ids[]',
    },
    ignoreKeys: ['categories'],
    defaults: {
        limit: 15,
    },
})

const { data, error } = await useApiFetch<Page<ProPage>>('/v1/articles', {
    query: l5Query,
    useLastModified: true,
})

const deferredProQuery = computed(() => ({
    ...l5Query.value,
    include: 'articles',
}))

const {
    data: deferredProData,
    pending: deferredProPending,
    execute: executeDeferredPro,
} = await useApiFetch<Page<ProPage>>('/v1/articles', {
    key: 'deferred-articles',
    query: deferredProQuery,
    server: false,
    immediate: false,
    watch: false,
})

const isDeferredProContentEnabled = ref(false)

async function loadDeferredProContent() {
    if (isDeferredProContentEnabled.value) return

    isDeferredProContentEnabled.value = true

    await executeDeferredPro()
}

watch(l5Query, async () => {
    if (!isDeferredProContentEnabled.value) return

    await executeDeferredPro()
})

const articles = computed(() => {
    if (!deferredProData.value) return null
    return deferredProData.value.data.articles
})

const pending = computed(() => deferredProPending.value)

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })

    useSchemaOrg([
        defineWebPage({
            inLanguage: 'ru',
            description: data.value.data.seo.seo_description,
            // mainEntity: [
            //     defineItemList({
            //         numberOfItems: data.value.data.articles.data.length,
            //         itemListElement: data.value.data.articles.data.map(
            //             (article) => {
            //                 const image = article.image
            //                     ? article.image[0]
            //                     : null
            //                 return defineListItem({
            //                     item: defineArticle({
            //                         '@type': 'BlogPosting',
            //                         name: article.name,
            //                         description: stripHtml(
            //                             article.preview_description
            //                         ),
            //                         headline: article.name,
            //                         author: defineOrganization({
            //                             '@id': organization['@id'],
            //                         }),
            //                         url:
            //                             useRoute().fullPath +
            //                             article.slug +
            //                             '/',
            //                         datePublished: article.published_at,
            //                         ...(image && {
            //                             image: getMediaLink(image.url),
            //                             thumbnailUrl: getMediaLink(image.url),
            //                         }),
            //                     }),
            //                 })
            //             }
            //         ),
            //     }),
            // ],
        }),
    ])
}
</script>
